import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type * as Cesium from 'cesium'
import { cdApi } from '../../api/changeDetection'
import type {
  CdRegionMode,
  CdDiff,
  CdDiffCategory,
  CdDiffVisible,
  CdObservation,
  CdProject,
  CdReport,
  CdReportFormat,
  CdVoxelStatus,
} from '../../types/changeDetection'
import CdNewProjectModal from './CdNewProjectModal'
import CdAddObservationModal from './CdAddObservationModal'
import CdDiffPanel from './CdDiffPanel'
import CdSplitView from './CdSplitView'
import { useCdAreaDraw } from './useCdAreaDraw'
import { useCdRegionOverlay } from './useCdRegionOverlay'
import CdRegionModeToggle from './CdRegionModeToggle'
import { useCdMapLayers, type CdCompare, type CdCompareKind } from './useCdMapLayers'
import type { CdActiveDiff } from './cdVoxelViz'

export type CdCompareMode = 'blink' | 'split'
export type { CdCompareKind }

interface Props {
  viewerRef: RefObject<Cesium.Viewer | null>
}

const OBS_ACTIVE: CdVoxelStatus[] = ['QUEUED', 'RUNNING']
const DIFF_ACTIVE = new Set(['QUEUED', 'RUNNING'])

const STATUS_STYLE: Record<CdVoxelStatus, string> = {
  NONE: 'bg-slate-100 text-slate-500',
  QUEUED: 'bg-amber-100 text-amber-700',
  RUNNING: 'bg-blue-100 text-blue-700',
  SUCCEEDED: 'bg-emerald-100 text-emerald-700',
  FAILED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-slate-100 text-slate-500',
}

interface CdToast {
  id: number
  kind: 'success' | 'warn' | 'error'
  text: string
  /** 있으면 클릭 시 해당 변화탐지 결과를 지도에 표시한다 */
  diffId?: number
}

const TOAST_MS = 9000
const OBS_PENDING: CdVoxelStatus[] = ['NONE', 'QUEUED', 'RUNNING']
const DIFF_FINISHED = new Set(['SUCCEEDED', 'FAILED', 'PARTIAL_FAILED'])

function toggle(list: number[], id: number): number[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id]
}

export default function CdSidebar({ viewerRef }: Props) {
  const { t } = useTranslation()
  const [projects, setProjects] = useState<CdProject[]>([])
  const [loadingProjects, setLoadingProjects] = useState(true)
  const [project, setProject] = useState<CdProject | null>(null)

  const [observations, setObservations] = useState<CdObservation[]>([])
  const [diffs, setDiffs] = useState<CdDiff[]>([])
  const [selected, setSelected] = useState<number[]>([])
  const [originalVisibleIds, setOriginalVisibleIds] = useState<number[]>([])
  const [voxelVisibleIds, setVoxelVisibleIds] = useState<number[]>([])
  const [shownDiffId, setShownDiffId] = useState<number | null>(null)
  const [diffVisible, setDiffVisible] = useState<CdDiffVisible>({ add: true, rem: true, chg: true })
  const [reportsByDiff, setReportsByDiff] = useState<Record<number, CdReport[]>>({})
  const [reportBusy, setReportBusy] = useState<Record<string, boolean>>({})
  const [compare, setCompare] = useState<{ diffId: number; mode: CdCompareMode; kinds: CdCompareKind[] } | null>(null)
  // 시계열 diff 의 시점 쌍 선택: 0 = 전체(겹쳐 표시), k = k번째 쌍만
  const [pairIndex, setPairIndex] = useState(0)
  const [mapContainer, setMapContainer] = useState<HTMLElement | null>(null)
  // 작업 완료 토스트 - 폴링 결과에서 진행 중이던 voxel/변화탐지가 끝나는 순간을 알린다.
  const [toasts, setToasts] = useState<CdToast[]>([])
  const toastSeq = useRef(0)
  const prevObsStatus = useRef<Map<number, CdVoxelStatus>>(new Map())
  const prevDiffStatus = useRef<Map<number, CdDiff['status']>>(new Map())
  // 관측 라벨/촬영일 인라인 수정
  const [editObsId, setEditObsId] = useState<number | null>(null)
  const [editObsDate, setEditObsDate] = useState('')
  const [editObsLabel, setEditObsLabel] = useState('')
  const [editObsError, setEditObsError] = useState('')
  const [voxelOpacity, setVoxelOpacity] = useState(0.5)
  const [pointSize, setPointSize] = useState(2)

  const [newProjectOpen, setNewProjectOpen] = useState(false)
  const [editProject, setEditProject] = useState<CdProject | null>(null)
  const [addObsOpen, setAddObsOpen] = useState(false)
  const [gpkgBusy, setGpkgBusy] = useState(false)

  useEffect(() => {
    const v = viewerRef.current
    if (v && !v.isDestroyed()) setMapContainer(v.container as HTMLElement)
  }, [viewerRef])

  // diff 분석 영역(WKT, 청록)과 voxel 생성 영역(WKT, 주황)은 각각 따로 그린다.
  const area = useCdAreaDraw(viewerRef, !!project)
  const regionDraw = useCdAreaDraw(viewerRef, !!project, '#f97316')
  useCdRegionOverlay(viewerRef, project?.regionWkt ?? null, !!project, project?.regionMode ?? 'INCLUDE')
  const [regionBusy, setRegionBusy] = useState(false)
  // 영역이 아직 없을 때 다음 업로드/저장에 쓸 모드. GPKG 는 기존 동작대로 제외, 직접 그리기는 포함이 기본.
  const [gpkgPendingMode, setGpkgPendingMode] = useState<CdRegionMode>('EXCLUDE')
  const [wktPendingMode, setWktPendingMode] = useState<CdRegionMode>('INCLUDE')

  const pollRef = useRef<number | null>(null)
  const gpkgInputRef = useRef<HTMLInputElement | null>(null)

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true)
    try {
      setProjects(await cdApi.listProjects())
    } catch {
      /* ignore */
    } finally {
      setLoadingProjects(false)
    }
  }, [])

  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  const loadWorkspace = useCallback(async () => {
    if (!project) return
    try {
      const [obs, df] = await Promise.all([
        cdApi.listObservations(project.id),
        cdApi.listDiffs(project.id),
      ])
      setObservations(obs)
      setDiffs(df)
    } catch {
      /* keep previous */
    }
  }, [project])

  useEffect(() => {
    setObservations([])
    setDiffs([])
    setSelected([])
    setOriginalVisibleIds([])
    setVoxelVisibleIds([])
    setShownDiffId(null)
    setDiffVisible({ add: true, rem: true, chg: true })
    setReportsByDiff({})
    setCompare(null)
    prevObsStatus.current.clear()
    prevDiffStatus.current.clear()
    setToasts([])
    loadWorkspace()
  }, [loadWorkspace])

  useEffect(() => {
    const active =
      observations.some((o) => OBS_ACTIVE.includes(o.voxelStatus)) ||
      diffs.some((d) => DIFF_ACTIVE.has(d.status))
    if (active && pollRef.current == null) {
      pollRef.current = window.setInterval(loadWorkspace, 3000)
    } else if (!active && pollRef.current != null) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => {
      if (pollRef.current != null) {
        window.clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [observations, diffs, loadWorkspace])

  const pushToast = useCallback((toast: Omit<CdToast, 'id'>) => {
    const id = ++toastSeq.current
    setToasts((prev) => [...prev.slice(-3), { ...toast, id }])
    window.setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), TOAST_MS)
  }, [])

  // voxel 생성: 여러 관측이 한 번에 끝나면(프로젝트 일괄 재생성 등) 토스트 하나로 합친다.
  useEffect(() => {
    const finished: CdObservation[] = []
    for (const o of observations) {
      const prev = prevObsStatus.current.get(o.id)
      if (prev && OBS_PENDING.includes(prev) && (o.voxelStatus === 'SUCCEEDED' || o.voxelStatus === 'FAILED')) {
        finished.push(o)
      }
      prevObsStatus.current.set(o.id, o.voxelStatus)
    }
    if (finished.length === 0) return
    const failed = finished.filter((o) => o.voxelStatus === 'FAILED')
    if (finished.length === 1) {
      const o = finished[0]
      const name = `${o.observedAt}${o.label ? ` · ${o.label}` : ''}`
      pushToast(
        o.voxelStatus === 'SUCCEEDED'
          ? { kind: 'success', text: t('cd.toast.voxelDone', { name }) }
          : {
              kind: 'error',
              text: o.errorMessage
                ? t('cd.toast.voxelFailedReason', { name, reason: o.errorMessage })
                : t('cd.toast.voxelFailed', { name }),
            },
      )
    } else {
      pushToast({
        kind: failed.length > 0 ? 'warn' : 'success',
        text:
          failed.length > 0
            ? t('cd.toast.voxelBatchMixed', { count: finished.length, failed: failed.length })
            : t('cd.toast.voxelBatchDone', { count: finished.length }),
      })
    }
  }, [observations, pushToast, t])

  // 변화탐지: 진행 중이던 diff 가 성공/실패/일부 실패로 끝나는 순간
  useEffect(() => {
    for (const d of diffs) {
      const prev = prevDiffStatus.current.get(d.id)
      prevDiffStatus.current.set(d.id, d.status)
      if (!prev || !DIFF_ACTIVE.has(prev) || !DIFF_FINISHED.has(d.status)) continue
      if (d.status === 'SUCCEEDED') {
        pushToast({ kind: 'success', text: t('cd.toast.diffDone', { name: d.name }), diffId: d.id })
      } else if (d.status === 'PARTIAL_FAILED') {
        pushToast({ kind: 'warn', text: t('cd.toast.diffPartial', { name: d.name }), diffId: d.id })
      } else {
        pushToast({
          kind: 'error',
          text: d.errorMessage
            ? t('cd.toast.diffFailedReason', { name: d.name, reason: d.errorMessage })
            : t('cd.toast.diffFailed', { name: d.name }),
        })
      }
    }
  }, [diffs, pushToast, t])

  const shownDiff = diffs.find((d) => d.id === shownDiffId) ?? null

  // 슬라이더/비교가 가리키는 diff — 비교 중이면 그 diff, 아니면 지도에 표시 중인 diff
  const pairDiff = diffs.find((d) => d.id === (compare?.diffId ?? shownDiffId)) ?? null
  const pairItems = useMemo(
    () =>
      (pairDiff?.items ?? [])
        .filter((it) => it.status === 'SUCCEEDED')
        .sort((x, y) => x.sourceObservedAt.localeCompare(y.sourceObservedAt)),
    [pairDiff],
  )
  const hasPairSlider = pairDiff?.type === 'TIME_SERIES' && pairItems.length >= 2
  const pairDiffId = pairDiff?.id ?? null
  useEffect(() => setPairIndex(0), [pairDiffId])
  // 선택된 쌍 (A/B diff 는 항상 첫 item, 시계열은 슬라이더가 0 이면 없음)
  const selectedPair =
    pairDiff?.type === 'A_B' ? (pairItems[0] ?? null) : pairIndex > 0 ? (pairItems[pairIndex - 1] ?? null) : null

  const activeDiff: CdActiveDiff | null = useMemo(() => {
    if (!shownDiff) return null
    const all = shownDiff.items.filter((it) => it.status === 'SUCCEEDED')
    const done =
      shownDiff.type === 'TIME_SERIES' && shownDiff.id === pairDiffId && selectedPair
        ? all.filter((it) => it.id === selectedPair.id)
        : all
    const sum = (pick: (it: CdDiff['items'][number]) => number | null) =>
      done.reduce((acc, it) => acc + (pick(it) ?? 0), 0)
    return {
      diffId: shownDiff.id,
      name: shownDiff.name,
      added: Math.round(sum((it) => it.addedVolume) * 100) / 100,
      removed: Math.round(sum((it) => it.removedVolume) * 100) / 100,
      changed: Math.round(sum((it) => it.changedVolume) * 100) / 100,
      realTilesets: done
        .map((it) => it.resultTilesetUrl)
        .filter((u): u is string => !!u),
      obsIds: [...new Set(done.flatMap((it) => [it.sourceObservationId, it.targetObservationId]))],
      visible: diffVisible,
    }
  }, [shownDiff, diffVisible, pairDiffId, selectedPair])

  const compareArg: CdCompare | null = useMemo(() => {
    if (!compare || !selectedPair || compare.diffId !== pairDiffId) return null
    return {
      mode: compare.mode,
      kinds: compare.kinds,
      sourceObsId: selectedPair.sourceObservationId,
      targetObsId: selectedPair.targetObservationId,
    }
  }, [compare, selectedPair, pairDiffId])

  const splitTarget =
    compareArg?.mode === 'split' ? observations.find((o) => o.id === compareArg.targetObsId) : undefined

  useCdMapLayers({
    viewerRef,
    enabled: !!project,
    observations,
    originalVisibleIds,
    voxelVisibleIds,
    activeDiff,
    center: project?.centerLon != null && project?.centerLat != null
      ? { lon: project.centerLon, lat: project.centerLat }
      : null,
    compare: compareArg,
    voxelOpacity,
    pointSize,
  })

  const forget = (id: number) => {
    setOriginalVisibleIds((p) => p.filter((x) => x !== id))
    setVoxelVisibleIds((p) => p.filter((x) => x !== id))
    setSelected((p) => p.filter((x) => x !== id))
  }

  const toggleSelect = (id: number) =>
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= 2) return [prev[1], id]
      return [...prev, id]
    })

  // 원본 3D Tiles 와 voxel 은 서로 영향 없이 독립적으로 켜고 끈다.
  const toggleVoxel = (id: number) => setVoxelVisibleIds((prev) => toggle(prev, id))

  const applyProjectUpdate = (p: CdProject) => {
    setProjects((prev) => prev.map((x) => (x.id === p.id ? p : x)))
    setProject((prev) => (prev && prev.id === p.id ? p : prev))
  }

  // ---------- voxel 생성 영역 ----------

  /** 영역을 바꾸면 기존 voxel 이 이전 영역 기준이므로, 저장 시 재생성 여부를 확인한다. */
  const confirmRevoxelize = async (reason: string) => {
    if (!project || observations.length === 0) return
    const ok = confirm(
      t('cd.region.revoxelConfirm', { reason, count: observations.length }),
    )
    if (!ok) return
    try {
      setObservations(await cdApi.revoxelizeProject(project.id))
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      alert(err.response?.data?.error ?? t('cd.region.revoxelFailed'))
    }
  }

  const handleRegionSave = async () => {
    if (!project || !regionDraw.wkt) return
    setRegionBusy(true)
    try {
      applyProjectUpdate(await cdApi.setRegionWkt(project.id, regionDraw.wkt, wktPendingMode))
      regionDraw.clear()
      await confirmRevoxelize(t('cd.region.savedReason'))
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      alert(err.response?.data?.error ?? t('cd.region.saveFailed'))
    } finally {
      setRegionBusy(false)
    }
  }

  const handleRegionRemove = async () => {
    if (!project) return
    setRegionBusy(true)
    try {
      applyProjectUpdate(await cdApi.deleteRegionWkt(project.id))
      await confirmRevoxelize(t('cd.region.removedReason'))
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      alert(err.response?.data?.error ?? t('cd.region.removeFailed'))
    } finally {
      setRegionBusy(false)
    }
  }

  const startRegionDraw = () => {
    area.clear()
    // 이미 저장된 영역을 다시 그릴 때는 그 모드를, 처음이면 선택한 모드를 쓴다.
    const mode = project?.regionWkt ? (project.regionMode ?? 'INCLUDE') : wktPendingMode
    regionDraw.start(mode === 'EXCLUDE' ? '#ef4444' : '#f97316')
  }

  /** 저장된 영역(GPKG/WKT)의 포함/제외 모드를 바꾸면 기존 voxel 이 맞지 않게 되므로 재생성 여부를 묻는다. */
  const handleRegionModeChange = async (mode: CdRegionMode) => {
    if (!project) return
    setRegionBusy(true)
    try {
      applyProjectUpdate(await cdApi.setRegionMode(project.id, mode))
      await confirmRevoxelize(
        t('cd.region.modeChangedReason', { mode: mode === 'EXCLUDE' ? t('common.exclude') : t('common.include') }),
      )
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      alert(err.response?.data?.error ?? t('cd.region.modeFailed'))
    } finally {
      setRegionBusy(false)
    }
  }

  // ---------- 리포트 ----------

  const loadReports = useCallback(async (diffId: number) => {
    try {
      const list = await cdApi.listReports(diffId)
      setReportsByDiff((prev) => ({ ...prev, [diffId]: list }))
    } catch {
      /* ignore */
    }
  }, [])

  const captureScreenshot = (diffId: number): string | null => {
    if (shownDiffId !== diffId) return null
    const v = viewerRef.current
    if (!v || v.isDestroyed()) return null
    try {
      v.render()
      return v.canvas.toDataURL('image/png')
    } catch {
      return null
    }
  }

  const handleCreateReport = async (diffId: number, format: CdReportFormat) => {
    const key = `${diffId}:${format}`
    setReportBusy((p) => ({ ...p, [key]: true }))
    try {
      await cdApi.createReport(diffId, { format, screenshotBase64: captureScreenshot(diffId) })
      await loadReports(diffId)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      alert(err.response?.data?.error ?? t('cd.report.createFailed'))
    } finally {
      setReportBusy((p) => ({ ...p, [key]: false }))
    }
  }

  const handleDownloadReport = async (diffId: number, report: CdReport) => {
    try {
      await cdApi.downloadReport(diffId, report.id, report.fileName)
    } catch {
      alert(t('cd.report.downloadFailed'))
    }
  }

  const handleDeleteReport = async (diffId: number, reportId: number) => {
    try {
      await cdApi.deleteReport(diffId, reportId)
      await loadReports(diffId)
    } catch {
      alert(t('cd.report.deleteFailed'))
    }
  }

  const handleGpkgPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !project) return
    setGpkgBusy(true)
    try {
      applyProjectUpdate(await cdApi.uploadRegionGpkg(project.id, file, gpkgPendingMode))
      area.clear() // GPKG 와 직접 선택 영역(WKT)은 함께 쓸 수 없다
    } catch (err: unknown) {
      const x = err as { response?: { data?: { error?: string } } }
      alert(x.response?.data?.error ?? t('cd.region.gpkgUploadFailed'))
    } finally {
      setGpkgBusy(false)
    }
  }

  const handleGpkgRemove = async () => {
    if (!project) return
    setGpkgBusy(true)
    try {
      applyProjectUpdate(await cdApi.deleteRegionGpkg(project.id))
    } catch {
      alert(t('common.deleteFailed'))
    } finally {
      setGpkgBusy(false)
    }
  }

  // ---------- 프로젝트 목록 화면 ----------
  if (!project) {
    return (
      <div className="flex-1 overflow-y-auto p-3">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-900">{t('cd.project.listTitle')}</h2>
          <button
            onClick={() => {
              setEditProject(null)
              setNewProjectOpen(true)
            }}
            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded transition-colors"
          >
            {t('cd.project.add')}
          </button>
        </div>

        {loadingProjects ? (
          <p className="text-xs text-gray-400 px-1">{t('common.loading')}</p>
        ) : projects.length === 0 ? (
          <div className="text-center text-xs text-gray-400 py-10">
            {t('cd.project.empty')}
          </div>
        ) : (
          <ul className="space-y-1.5">
            {projects.map((p) => (
              <li
                key={p.id}
                onClick={() => setProject(p)}
                className="group border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-400 hover:bg-blue-50/40 cursor-pointer transition"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setEditProject(p)
                        setNewProjectOpen(true)
                      }}
                      className="text-[11px] text-gray-400 hover:text-gray-700"
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        if (!confirm(t('cd.project.deleteConfirm', { name: p.name }))) return
                        await cdApi.deleteProject(p.id)
                        setProjects((prev) => prev.filter((x) => x.id !== p.id))
                      }}
                      className="text-[11px] text-gray-400 hover:text-red-600"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {t('cd.project.meta', { count: p.observationCount, date: p.createdAt?.slice(0, 10) ?? '' })}
                </p>
              </li>
            ))}
          </ul>
        )}

        <CdNewProjectModal
          open={newProjectOpen}
          project={editProject}
          onClose={() => setNewProjectOpen(false)}
          onSaved={(saved) =>
            setProjects((prev) => {
              const exists = prev.some((x) => x.id === saved.id)
              return exists ? prev.map((x) => (x.id === saved.id ? saved : x)) : [saved, ...prev]
            })
          }
        />
      </div>
    )
  }

  // ---------- 프로젝트 워크스페이스 화면 ----------
  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-4">
      <div>
        <button onClick={() => setProject(null)} className="text-xs text-gray-500 hover:text-gray-800">
          {t('cd.project.back')}
        </button>
        <h2 className="mt-1 text-sm font-bold text-gray-900 truncate">{project.name}</h2>

        <div className="mt-2 text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-600">{t('cd.region.gpkgLabel')}</span>
          <CdRegionModeToggle
            value={project.hasRegionGpkg ? project.regionMode : gpkgPendingMode}
            onChange={project.hasRegionGpkg ? handleRegionModeChange : setGpkgPendingMode}
            disabled={regionBusy || gpkgBusy}
          />
          {project.hasRegionGpkg ? (
            <>
              <span className="text-gray-700 truncate max-w-[150px]" title={project.regionGpkgFileName ?? ''}>
                {project.regionGpkgFileName ?? 'region.gpkg'}
              </span>
              <button
                onClick={handleGpkgRemove}
                disabled={gpkgBusy}
                className="text-red-500 hover:text-red-700 disabled:opacity-40"
              >
                {t('common.delete')}
              </button>
            </>
          ) : (
            <button
              onClick={() => gpkgInputRef.current?.click()}
              disabled={gpkgBusy || !!project.regionWkt}
              title={project.regionWkt ? t('cd.region.blockedByWkt') : undefined}
              className="text-blue-600 hover:text-blue-800 disabled:opacity-40"
            >
              {gpkgBusy ? t('common.uploading') : t('common.addFile')}
            </button>
          )}
          <input ref={gpkgInputRef} type="file" accept=".gpkg" hidden onChange={handleGpkgPick} />
        </div>

        <div className="mt-1 text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-600">{t('cd.region.drawLabel')}</span>
          <CdRegionModeToggle
            value={project.regionWkt ? project.regionMode : wktPendingMode}
            onChange={project.regionWkt ? handleRegionModeChange : setWktPendingMode}
            disabled={regionBusy || project.hasRegionGpkg || regionDraw.drawing}
          />
          {project.hasRegionGpkg ? (
            <span className="text-gray-400">{t('cd.region.gpkgInUse')}</span>
          ) : regionDraw.drawing ? (
            <span className="text-blue-600">{t('cd.region.drawHint')}</span>
          ) : regionDraw.wkt ? (
            <>
              <button
                onClick={handleRegionSave}
                disabled={regionBusy}
                className="font-medium text-blue-600 hover:text-blue-800 disabled:opacity-40"
              >
                {regionBusy ? t('common.saving') : t('common.save')}
              </button>
              <button onClick={startRegionDraw} className="text-gray-500 hover:text-gray-800">
                {t('common.redraw')}
              </button>
              <button onClick={regionDraw.clear} className="text-gray-400 hover:text-gray-700">
                {t('common.cancel')}
              </button>
            </>
          ) : project.regionWkt ? (
            <>
              <span className={project.regionMode === 'EXCLUDE' ? 'text-red-500' : 'text-orange-600'}>
                {t('cd.region.isSet', { mode: project.regionMode === 'EXCLUDE' ? t('common.exclude') : t('common.include') })}
              </span>
              <button onClick={startRegionDraw} disabled={regionBusy} className="text-blue-600 hover:text-blue-800 disabled:opacity-40">
                {t('common.redraw')}
              </button>
              <button onClick={handleRegionRemove} disabled={regionBusy} className="text-red-500 hover:text-red-700 disabled:opacity-40">
                {t('common.delete')}
              </button>
            </>
          ) : (
            <button onClick={startRegionDraw} className="text-blue-600 hover:text-blue-800">
              {t('common.drawArea')}
            </button>
          )}
        </div>
      </div>

      {/* 관측 데이터 */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-xs font-bold text-gray-700">{t('cd.obs.title', { count: observations.length })}</h3>
          <button
            onClick={() => setAddObsOpen(true)}
            className="text-[11px] bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded"
          >
            {t('cd.obs.addDate')}
          </button>
        </div>

        {observations.length > 0 && (
          <div className="mb-2 space-y-1 rounded-lg border border-gray-200 bg-gray-50/60 px-2.5 py-1.5">
            <label className="flex items-center gap-2 text-[11px] text-gray-600">
              <span className="w-16 shrink-0">{t('cd.obs.voxelOpacity')}</span>
              <input
                type="range"
                min={0.1}
                max={1}
                step={0.05}
                value={voxelOpacity}
                onChange={(e) => setVoxelOpacity(Number(e.target.value))}
                className="flex-1 accent-cyan-600"
              />
              <span className="w-8 text-right text-gray-400">{Math.round(voxelOpacity * 100)}%</span>
            </label>
            {observations.some((o) => o.datasetType === 'POINT_CLOUD') && (
              <label className="flex items-center gap-2 text-[11px] text-gray-600">
                <span className="w-16 shrink-0">{t('cd.obs.pointSize')}</span>
                <input
                  type="range"
                  min={1}
                  max={10}
                  step={1}
                  value={pointSize}
                  onChange={(e) => setPointSize(Number(e.target.value))}
                  className="flex-1 accent-blue-600"
                />
                <span className="w-8 text-right text-gray-400">{pointSize}px</span>
              </label>
            )}
          </div>
        )}

        {observations.length === 0 ? (
          <p className="text-[11px] text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded-lg">
            {t('cd.obs.empty')}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {observations.map((o) => {
              const showOriginal = originalVisibleIds.includes(o.id)
              const showVoxel = voxelVisibleIds.includes(o.id)
              return (
                <li key={o.id} className="border border-gray-200 rounded-lg px-2.5 py-2">
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      disabled={o.voxelStatus !== 'SUCCEEDED'}
                      checked={selected.includes(o.id)}
                      onChange={() => toggleSelect(o.id)}
                    />
                    <div className="flex-1 min-w-0">
                      {editObsId === o.id ? (
                        <form
                          className="space-y-1"
                          onSubmit={async (e) => {
                            e.preventDefault()
                            setEditObsError('')
                            try {
                              const u = await cdApi.updateObservation(o.id, {
                                observedAt: editObsDate,
                                label: editObsLabel.trim() || null,
                              })
                              setObservations((prev) =>
                                prev.map((x) => (x.id === o.id ? u : x)).sort((a, b) => a.observedAt.localeCompare(b.observedAt)),
                              )
                              setEditObsId(null)
                            } catch (err: unknown) {
                              const x = err as { response?: { data?: { error?: string } } }
                              setEditObsError(x.response?.data?.error ?? t('cd.obs.editFailed'))
                            }
                          }}
                        >
                          <input
                            type="date"
                            required
                            value={editObsDate}
                            onChange={(e) => setEditObsDate(e.target.value)}
                            className="w-full rounded border border-gray-300 px-1.5 py-0.5 text-xs"
                          />
                          <input
                            value={editObsLabel}
                            maxLength={200}
                            placeholder={t('cd.obs.labelPlaceholder')}
                            onChange={(e) => setEditObsLabel(e.target.value)}
                            className="w-full rounded border border-gray-300 px-1.5 py-0.5 text-xs"
                          />
                          <div className="flex gap-2 text-[11px]">
                            <button type="submit" className="font-medium text-blue-600">
                              {t('common.save')}
                            </button>
                            <button type="button" onClick={() => setEditObsId(null)} className="text-gray-400">
                              {t('common.cancel')}
                            </button>
                          </div>
                          {editObsError && <p className="text-[10px] text-red-600">{editObsError}</p>}
                        </form>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-medium text-gray-800">{o.observedAt}</span>
                            <span className={`text-[10px] px-1 py-0.5 rounded ${STATUS_STYLE[o.voxelStatus]}`}>
                              {t(`cd.status.voxel.${o.voxelStatus}`)}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-400 truncate">
                            {o.label ? `${o.label} · ` : ''}
                            {o.meshDataName}
                          </p>
                        </>
                      )}
                      <div className="mt-1 flex flex-wrap gap-1">
                        <button
                          onClick={() => setOriginalVisibleIds((prev) => toggle(prev, o.id))}
                          disabled={!o.originalTilesetUrl}
                          className={`text-[10px] px-1.5 py-0.5 rounded border disabled:opacity-40 ${
                            showOriginal ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 text-gray-600'
                          }`}
                        >
                          3D Tiles
                        </button>
                        <button
                          onClick={() => toggleVoxel(o.id)}
                          disabled={o.voxelStatus !== 'SUCCEEDED'}
                          className={`text-[10px] px-1.5 py-0.5 rounded border disabled:opacity-40 ${
                            showVoxel ? 'bg-cyan-600 border-cyan-600 text-white' : 'border-gray-300 text-gray-600'
                          }`}
                        >
                          voxel
                        </button>
                        {(o.voxelStatus === 'FAILED' || o.voxelStatus === 'CANCELLED' || o.voxelStatus === 'NONE') && (
                          <button
                            onClick={async () => {
                              const u = await cdApi.revoxelize(o.id)
                              setObservations((prev) => prev.map((x) => (x.id === o.id ? u : x)))
                            }}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-blue-600"
                          >
                            {t('cd.obs.rerun')}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditObsId(o.id)
                            setEditObsDate(o.observedAt)
                            setEditObsLabel(o.label ?? '')
                            setEditObsError('')
                          }}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-gray-600"
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm(t('cd.obs.deleteConfirm'))) return
                            await cdApi.deleteObservation(o.id)
                            setObservations((prev) => prev.filter((x) => x.id !== o.id))
                            forget(o.id)
                          }}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-gray-400 hover:text-red-600"
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                      {o.errorMessage && <p className="text-[10px] text-red-600 mt-0.5">{o.errorMessage}</p>}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* 변화탐지 */}
      <CdDiffPanel
        projectId={project.id}
        observations={observations}
        selectedIds={selected}
        diffs={diffs}
        shownDiffId={shownDiffId}
        diffVisible={diffVisible}
        onChanged={loadWorkspace}
        onToggleShow={(id) =>
          setShownDiffId((prev) => {
            const next = prev === id ? null : id
            if (next !== null) setDiffVisible({ add: true, rem: true, chg: true })
            return next
          })
        }
        onToggleDiffVisible={(cat: CdDiffCategory) =>
          setDiffVisible((prev) => ({ ...prev, [cat]: !prev[cat] }))
        }
        reportsByDiff={reportsByDiff}
        reportBusy={reportBusy}
        onLoadReports={loadReports}
        onCreateReport={handleCreateReport}
        onDownloadReport={handleDownloadReport}
        onDeleteReport={handleDeleteReport}
        mapContainer={mapContainer}
        areaWkt={area.wkt}
        areaDrawing={area.drawing}
        onStartDrawArea={(mode) => {
          regionDraw.clear()
          area.start(mode === 'EXCLUDE' ? '#ef4444' : '#06b6d4')
        }}
        onClearArea={area.clear}
        compare={compare}
        onSetCompare={(diffId, mode, kinds) => {
          setCompare(mode === null ? null : { diffId, mode, kinds })
          // 시계열은 비교할 쌍이 필요하므로 '전체' 상태면 첫 쌍으로 이동
          if (mode !== null && pairIndex === 0 && diffs.find((d) => d.id === diffId)?.type === 'TIME_SERIES') {
            setPairIndex(1)
          }
        }}
      />

      {mapContainer && compareArg?.mode === 'split' && (
        <CdSplitView
          viewerRef={viewerRef}
          container={mapContainer}
          targets={compareArg.kinds.flatMap((kind) => {
            const url = kind === 'voxel' ? splitTarget?.voxelTilesetUrl : splitTarget?.originalTilesetUrl
            return url ? [{ kind, url }] : []
          })}
          voxelOpacity={voxelOpacity}
          pointCloud={splitTarget?.datasetType === 'POINT_CLOUD'}
          pointSize={pointSize}
          labelA={selectedPair?.sourceObservedAt ?? ''}
          labelB={selectedPair?.targetObservedAt ?? ''}
        />
      )}

      {mapContainer &&
        toasts.length > 0 &&
        createPortal(
          <div className="pointer-events-none absolute left-1/2 top-14 z-40 flex w-[min(420px,80%)] -translate-x-1/2 flex-col gap-2">
            {toasts.map((toast) => (
              <div
                key={toast.id}
                role="status"
                onClick={() => {
                  if (toast.diffId != null) setShownDiffId(toast.diffId)
                  setToasts((prev) => prev.filter((x) => x.id !== toast.id))
                }}
                className={`pointer-events-auto cursor-pointer rounded-lg px-3 py-2 text-xs font-medium text-white shadow-xl ${
                  toast.kind === 'success' ? 'bg-emerald-600/95' : toast.kind === 'warn' ? 'bg-amber-500/95' : 'bg-red-600/95'
                }`}
              >
                {toast.kind === 'success' ? '✅ ' : toast.kind === 'warn' ? '⚠️ ' : '❌ '}
                {toast.text}
              </div>
            ))}
          </div>,
          mapContainer,
        )}

      {mapContainer &&
        hasPairSlider &&
        createPortal(
          <div className="absolute bottom-6 left-1/2 z-30 w-[min(440px,60%)] -translate-x-1/2 rounded-lg bg-slate-900/90 px-4 py-2 text-white shadow-xl">
            <div className="mb-1 flex items-center justify-between text-[11px]">
              <span className="font-semibold text-slate-300">{t('cd.slider.label')}</span>
              <span className="font-medium">
                {selectedPair
                  ? `${selectedPair.sourceObservedAt} → ${selectedPair.targetObservedAt} (${pairIndex}/${pairItems.length})`
                  : t('cd.slider.overlay', { count: pairItems.length })}
              </span>
            </div>
            <input
              type="range"
              min={compare ? 1 : 0}
              max={pairItems.length}
              step={1}
              value={pairIndex}
              onChange={(e) => setPairIndex(Number(e.target.value))}
              aria-label={t('cd.slider.aria')}
              className="w-full accent-blue-500"
            />
            <div className="mt-0.5 flex justify-between text-[9px] text-slate-400">
              <span>{compare ? pairItems[0].sourceObservedAt : t('common.all')}</span>
              <span>{pairItems[pairItems.length - 1].targetObservedAt}</span>
            </div>
          </div>,
          mapContainer,
        )}

      <CdAddObservationModal
        open={addObsOpen}
        projectId={project.id}
        onClose={() => setAddObsOpen(false)}
        onAdded={(obs) =>
          setObservations((prev) => [...prev, obs].sort((a, b) => a.observedAt.localeCompare(b.observedAt)))
        }
      />
    </div>
  )
}
